/**
 * App-lock (Phase 4): re-prompt the device PIN after inactivity or when the app returns to the
 * foreground, even while the session is alive — protects against a picked-up unlocked phone with a
 * live session (design §5.3). Only active when a device PIN is enrolled on this device; unlock is a
 * server-verified re-proof (devicePinApi.verify), not a client-only gate.
 *
 * Singleton state (module scope) so App.vue and the lock overlay share one `locked`.
 */
import { ref } from 'vue';
import { devicePinApi } from '@/api/devicePin';

const INACTIVITY_MS = 5 * 60 * 1000;   // 5 min idle → relock

const locked = ref(false);
const enabled = ref(false);            // a device PIN is set → app-lock is armed
let lastActivity = Date.now();
let idleTimer: ReturnType<typeof setInterval> | null = null;
let wired = false;

function markActivity() {
  lastActivity = Date.now();
}

function tick() {
  if (enabled.value && !locked.value && Date.now() - lastActivity >= INACTIVITY_MS) {
    locked.value = true;
  }
}

function onVisibility() {
  // Returning to foreground after being hidden → relock (unlocked phone left in a pocket).
  if (document.visibilityState === 'visible' && enabled.value) locked.value = true;
}

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'mousemove', 'wheel', 'scroll'];

export function useAppLock() {
  /**
   * Arm the lock for the current device if a PIN is enrolled. Call after auth is established.
   * @param lockNow true on a COLD app open / reload with a restored session (banking behavior:
   *        re-prompt on every cold open) — but NOT right after a fresh interactive login.
   */
  async function init(lockNow = false) {
    try {
      const s = await devicePinApi.status();
      enabled.value = s.enabled;
    } catch {
      enabled.value = false;
    }
    if (enabled.value) {
      wire();
      if (lockNow) locked.value = true;   // cold-start/reload → require PIN before use
    }
  }

  /** Re-check enrollment (e.g. after the user sets/clears a device PIN mid-session). */
  const refresh = () => init(false);

  function wire() {
    if (wired) return;
    wired = true;
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, markActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    idleTimer = setInterval(tick, 15_000);
    markActivity();
  }

  async function unlock(pin: string): Promise<{ ok: boolean; status: string }> {
    const r = await devicePinApi.verify(pin);
    if (r.ok) { locked.value = false; markActivity(); }
    return r;
  }

  /** Drop the lock after an out-of-band re-proof (e.g. biometric/passkey) — stays armed for next time. */
  function markUnlocked() { locked.value = false; markActivity(); }

  /** Reset on logout — disarm + unlock so the login screen isn't covered; unwire listeners. */
  function reset() {
    enabled.value = false;
    locked.value = false;
    if (idleTimer) { clearInterval(idleTimer); idleTimer = null; }
    if (wired) {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, markActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      wired = false;
    }
  }

  return { locked, enabled, init, refresh, unlock, markUnlocked, reset };
}
